import { useMutation } from '@apollo/client/react'
import { Formik } from 'formik'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useFormikHelpers } from '../../../util/useFormikHelpers'
import styled from 'styled-components'

import { towingServiceTypeOptions } from '../../../common/options'
import { CustomerType, LockMessage, LockedForName, Maybe, TowingRecordPaymentType, TowingRecordStatus, TowingRecordType, TypeIdentifier } from '../../../common/types'
import { Button } from '../../../components/Button'
import { ButtonColumn, ButtonRow, FormPadder, Page, SimpleRow } from '../../../components/layout'
import { Text } from '../../../components/layout'
import { ScrollToError } from '../../../components/layout/form/ScrollToError'
import { FormikForm as Form } from '../../../components/layout/form/style'
import { Modal } from '../../../components/Modal'
import { Navigate } from '../../../components/NavigateTo'
import { setErrorNotification, setSuccessNotification } from '../../../components/notification'
import { Loading } from '../../../components/responses'
import { setErrorNotifications, setFatalErrorNotification } from '../../../components/responses/setErrorNotifications'
import { useHistoryPush } from '../../../components/router'
import { initialSosServiceOrder } from '../../../components/SosSubForm/types'
import { maybeStopTrackingRecord } from '../../../components/TrackedJobs/util'
import { persistentDelete, persistentSave } from '../../../util/localStorage/localStorage'
import { translateInputOptions, formatMessageSafe } from '../../../util/translateInputOptions'
import { EditCustomer } from '../../Customer/EditCustomer'
import { CustomerFormValues } from '../../Customer/types'
import { DispatchStore, StateStore } from '../../StoreProvider'
import { STORED_TOWING_RECORD, handleNetworkError } from '../../StoreProvider/OfflineMode'
import { shouldDisableOperator } from '../../TowingOrderPage/EditTowingOrder/EditTowingOrderForm'
import { BillingConfirmation } from '../components/billing/BillingConfirmation'
import { shouldShowBillingConfirmation } from '../components/billing/BillingConfirmation/shouldShowBillingConfirmation'
import { isPaymentLinkAlreadyActive } from '../components/billing/TowingRecordBilling/types'
import { getInitialValues } from './initialValues'
import { CONVERT_TO_SOS_RECORD, EDIT_TOWING_RECORD } from './mutation/mutation'
import { toMutationVariables } from './mutation/toMutationVariables'
import { ConvertToSosRecordResponse, EditTowingRecordResponse } from './mutation/types'
import { TowingRecordFormFields } from './TowingRecordFormFields'
import { Billing, EditTowingRecord, EditTowingRecordFormValues } from './types'
import { validate } from './validate'
import { getStoredCompanyId } from '../../../graphql-client/storedState'
import { useSocket } from '../../../util/useSocket'
import { WarningBanner } from '../../../components/TowingCommon/WarningBanner'
import { defineMessages, useIntl } from 'react-intl'
import { useSettings } from '../../Parameters'

interface EditTowingRecordProps {
  towingRecord: EditTowingRecord
  paymentType: Maybe<TowingRecordPaymentType>
  scrollTo: string | undefined
  refetch?: () => Promise<any>
}

const convertModalMessage = `Täydennä puuttuvat tiedot. Kun kaikki tiedot on täytetty ja painat tallenna, niin työ muunnetaan SOS työksi.Tallennuksen jälkeen täytä reittipisteet, sinut ohjataan ensimmäiselle reitille automaattisesti.`

const EditTowingRecordPageIntl: React.FunctionComponent<EditTowingRecordProps> = ({
  towingRecord,
  paymentType,
  scrollTo,
  refetch,
}) => {
  const [serviceOrderDraft, setServiceOrderDraft] = useState<boolean>(false)
  const [isWebEditing, setIsWebEditing] = useState<LockedForName>(towingRecord.locked)
  const historyPush = useHistoryPush()
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)
  const [displayConfirmationPage, setShowConfirmationPage] = useState<boolean>(false)

  const { state } = useContext(StateStore)
  const { dispatch } = useContext(DispatchStore)
  const socket = useSocket()
  const settings = useSettings()

  const [convertToSosRecord, { loading }] = useMutation<ConvertToSosRecordResponse>(CONVERT_TO_SOS_RECORD, {
    onCompleted({ convertToSosRecord }) {
      if (convertToSosRecord.__typename === 'ConvertToSosRecordSuccess') {
        setSuccessNotification(
          formatMessage(messages.toSosRecordSuccessTitle),
          formatMessage(messages.toSosRecordSuccessMessage)
        )
        persistentDelete(STORED_TOWING_RECORD)
        historyPush(`/towing-record/${towingRecord.id}/start`)
        return
      }
      setErrorNotifications({ data: convertToSosRecord })
    },
    onError(error) {
      setFatalErrorNotification(error)
    },
  })

  const [editTowingRecord, mutationResult] = useMutation<EditTowingRecordResponse>(EDIT_TOWING_RECORD, {
    onCompleted({ editTowingRecord }) {
      if ('towingRecord' in editTowingRecord) {
        maybeStopTrackingRecord(editTowingRecord.towingRecord)
      }
      if (editTowingRecord.__typename === 'EditTowingRecordSuccess') {
        if (shouldShowBillingConfirmation(editTowingRecord.towingRecord.billing)) {
          setShowConfirmationPage(true)
          return
        }

        historyPush(`/towing-record/${towingRecord.id}`)
        setSuccessNotification(
          formatMessage(messages.towingRecordSuccessTitle),
          formatMessage(messages.towingRecordSuccessMessage)
        )
        persistentDelete(STORED_TOWING_RECORD)
        return
      }
      if (editTowingRecord.__typename === 'EditTowingRecordWithBillingUpdateSuccess') {
        const { addBilling } = editTowingRecord

        persistentDelete(STORED_TOWING_RECORD)
        if (addBilling.__typename === 'AddBillingSuccess') {
          if (addBilling.billing.type === TowingRecordPaymentType.online_payment) {
            historyPush(`/towing-record/${towingRecord.id}`)
            setSuccessNotification(
              formatMessage(messages.paymentLinkSentTitle),
              formatMessage(messages.paymentLinkSentMessage),
            )
            return
          }
          setShowConfirmationPage(true)
          return
        }
        setErrorNotification(
          formatMessage(messages.addBillingErrorTitle),
          formatMessage(messages.addBillingErrorMessage)
        )
        return
      }

      setErrorNotifications({ data: editTowingRecord })
    },
    onError(error) {
      setFatalErrorNotification(error)

      if (!state.offlineMode) {
        return handleNetworkError(dispatch, state.unSavedJobCount + 1)
      }
      handleNetworkError(dispatch, state.unSavedJobCount)
    },
  })

  const handleSubmit = useCallback(
    async (values: EditTowingRecordFormValues) => {
      if (serviceOrderDraft && towingRecord.type !== TowingRecordType.sos) {
        await convertToSosRecord({ variables: toMutationVariables(values, serviceOrderDraft) })
        setServiceOrderDraft(false)
        return
      }
      await editTowingRecord({ variables: toMutationVariables(values) })
    },
    [editTowingRecord, convertToSosRecord, serviceOrderDraft, towingRecord.type]
  )

  const defaultItemsIncludeVat = settings ? settings.defaultItemsIncludeVat : true
  const initialValues = getInitialValues(towingRecord, defaultItemsIncludeVat, paymentType)
  const agreements = settings ? settings.agreements : []
  const canConvertToSos =
    settings &&
    settings.modules &&
    settings.modules.sos &&
    towingRecord.typeIdentifier !== TypeIdentifier.lahitapiola &&
    towingRecord.status === TowingRecordStatus.unfinished

  if (agreements.length > 0) {
    initialValues.signature.agreements = agreements.map(a => {
      return {
        id: a.id,
        text: a.text,
        isAgreed: true,
      }
    })
  }

  const translatedServiceTypeOptions = translateInputOptions(towingServiceTypeOptions, formatMessage)
  const billing = getNewBillingData(mutationResult.data ?? undefined)
  const disableOperator = shouldDisableOperator(state.currentUser)

  useEffect(()=>{
    const companyId = getStoredCompanyId();
    if(!companyId){
      return;
    }

    const callback = (event:string) => {
      const data: LockMessage = JSON.parse(event);
      setIsWebEditing(data.userName ? data.userName : null)
      // console.log('MESSAGE RECEIVED: ', data)
    }

    socket.on(`company-${companyId}.towing_record-${towingRecord.id}.locked`, callback);
    socket.on(`company-${companyId}.towing_record-${towingRecord.id}.unlocked`, callback);
    return ()=>{
      console.log('removing event listeners from editTowingOrderPage');
      socket.removeEventListener(`company-${companyId}.towing_record-${towingRecord.id}.locked`, callback);
      socket.removeEventListener(`company-${companyId}.towing_record-${towingRecord.id}.unlocked`, callback);
    }
  },[socket, setIsWebEditing, towingRecord.id]);

  return (
    <Formik
      validate={validate}
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {() => (
        <EditTowingRecordContent
          towingRecord={towingRecord}
          mutationLoading={mutationResult.loading || loading}
          isWebEditing={isWebEditing}
          serviceOrderDraft={serviceOrderDraft}
          setServiceOrderDraft={setServiceOrderDraft}
          displayConfirmationPage={displayConfirmationPage}
          setShowConfirmationPage={setShowConfirmationPage}
          billing={billing}
          initialValues={initialValues}
          translatedServiceTypeOptions={translatedServiceTypeOptions}
          disableOperator={disableOperator}
          canConvertToSos={canConvertToSos}
          historyPush={historyPush}
          formatMessage={formatMessage}
          scrollTo={scrollTo}
          refetch={refetch}
        />
      )}
    </Formik>
  )
}

interface EditTowingRecordContentProps {
  towingRecord: EditTowingRecord
  mutationLoading: boolean
  isWebEditing: LockedForName
  serviceOrderDraft: boolean
  setServiceOrderDraft: (v: boolean) => void
  displayConfirmationPage: boolean
  setShowConfirmationPage: (v: boolean) => void
  billing: Billing | null
  initialValues: EditTowingRecordFormValues
  translatedServiceTypeOptions: object[]
  disableOperator: boolean
  canConvertToSos: boolean | null | undefined
  historyPush: (path: string) => void
  formatMessage: any
  scrollTo: string | undefined
  refetch?: () => Promise<any>
}

const EditTowingRecordContent: React.FunctionComponent<EditTowingRecordContentProps> = ({
  towingRecord,
  mutationLoading,
  isWebEditing,
  serviceOrderDraft,
  setServiceOrderDraft,
  displayConfirmationPage,
  setShowConfirmationPage,
  billing,
  initialValues,
  translatedServiceTypeOptions,
  disableOperator,
  canConvertToSos,
  historyPush,
  formatMessage,
  scrollTo,
  refetch,
}) => {
  const [showCustomerEditForm, setShowCustomerEditForm] = useState<boolean>(false)
  const [showConvertToSosModal, setShowConvertToSosModal] = useState<boolean>(false)
  const [showPaymentLinkSaveConfirm, setShowPaymentLinkSaveConfirm] = useState<boolean>(false)
  const { values, setValues, dirty, submitForm } = useFormikHelpers<EditTowingRecordFormValues>()
  const settings = useSettings()

  const setOrderer = (o: CustomerFormValues) => {
    if (!values.orderer) {
      return
    }

    const currentOrdererType = values.orderer.type
    values.orderer = Object.assign({}, o)
    values.orderer.type = currentOrdererType

    if (settings && settings.notifications.enabled) {
      switch (o.type) {
        case CustomerType.person:
          if (o.personCustomer) {
            if (o.personCustomer.notificationMethod === 'default') {
              values.notificationMethod = settings.notifications.method
            } else {
              values.notificationMethod = o.personCustomer.notificationMethod
            }
          }
          break
        case CustomerType.company:
          if (o.companyCustomer) {
            if (o.companyCustomer.notificationMethod === 'default') {
              values.notificationMethod = settings.notifications.method
            } else {
              values.notificationMethod = o.companyCustomer.notificationMethod
            }
          }
          break
      }
    }

    setValues(values)
  }

  const hasFormChanged = () => {
    return dirty || values.billing.paymentDetails.id === null
  }

  const setOrdererFromTrafi = (o: CustomerFormValues) => {
    values.orderer = Object.assign({}, o)
    setValues(values)
  }

  const save = () => {
    persistentSave('towingRecord', values)

    if (values.sosServiceOrder && serviceOrderDraft) {
      const { coords: fromCoords } = values.from
      const { insuranceCompany, deliverable, cause } = values.sosServiceOrder
      let errFields = ''
      let coordsErr = false

      if (!insuranceCompany) {
        errFields += 'Vakuutusyhtiö'
      }

      if (!deliverable) {
        errFields += errFields.length > 0 ? `, Arvioitu toimenpide` : 'Arvioitu toimenpide'
      }

      if (!cause) {
        errFields += errFields.length > 0 ? `, Arvioitu vika` : 'Arvioitu vika'
      }

      if (!fromCoords.lat || !fromCoords.long) {
        coordsErr = true
        errFields +=
          errFields.length > 0
            ? `. Kohteen koordinaatteja ei saatu haettua`
            : 'Kohteen koordinaatteja ei saatu haettua'
      }

      if (coordsErr) {
        errFields += '. Hae osoite kartalta tai valitse osoite osoitehaun ehdotuksista.'
      }

      if (errFields.length > 0) {
        return setErrorNotification('Virhe', `Tarkista seuraavat tiedot: ${errFields}`)
      }
    }

    if (
      values.paymentType === TowingRecordPaymentType.online_payment &&
      !isPaymentLinkAlreadyActive(values.paymentType, values.billing.status)
    ) {
      if (!values.billing.paymentDetails.debtorPhone?.trim()) {
        return setErrorNotification(
          'Virhe',
          formatMessage({
            id: 'towingRecord.billing.paymentLink.noPhone',
            defaultMessage: 'Syötä puhelinnumero',
          }),
        )
      }
      setShowPaymentLinkSaveConfirm(true)
      return
    }

    submitForm()
  }

  const onSetSosServiceOrder = () => {
    const t = 'sosServiceOrder'
    values[t] = Object.assign({}, initialSosServiceOrder)
    setValues(values)
  }

  return (
    <Page noPadding>
      <Loading loading={mutationLoading} />
      {!!isWebEditing && (
        <WarningBanner
          title='Työ lukittu'
          message={`${isWebEditing} on avannut työn muokattavaksi. Tallentamattomat muutokset voivat hävitä ellei niitä tallenna heti.`}>
        </WarningBanner>
      )}
      {showCustomerEditForm && values.orderer && (
        <EditCustomer
          orderer={values.orderer}
          onClose={() => setShowCustomerEditForm(false)}
          onUpdate={orderer => {
            setOrderer(orderer)
            setShowCustomerEditForm(false)
          }}
        />
      )}

      {showPaymentLinkSaveConfirm && (
        <Modal
          label={formatMessage({
            id: 'towingRecord.billing.paymentLink.saveConfirm.label',
            defaultMessage: 'Lähetä maksulinkki',
          })}
          message={formatMessage({
            id: 'towingRecord.billing.paymentLink.saveConfirm.message',
            defaultMessage:
              'Maksulinkin lähettäminen lukitsee maksurivit, eikä niitä voi muokata enää. Haluatko jatkaa?',
          })}
          confirmButtonCategory="save"
          onConfirm={() => {
            setShowPaymentLinkSaveConfirm(false)
            submitForm()
          }}
          close={() => setShowPaymentLinkSaveConfirm(false)}
          closeLabel="Peruuta"
          confirmLabel={formatMessage({
            id: 'towingRecord.billing.paymentLink.saveConfirm.confirm',
            defaultMessage: 'Lähetä',
          })}
        />
      )}
      {showConvertToSosModal && (
        <Modal
          label="Muunna SOS työksi"
          message={convertModalMessage}
          close={() => setShowConvertToSosModal(false)}
          onConfirm={() => {
            setServiceOrderDraft(true)
            onSetSosServiceOrder()
          }}
          confirmLabel="Muunna SOS työksi"
          closeLabel="Peruuta"
        />
      )}

      {billing && displayConfirmationPage ? (
        <BillingConfirmation
          billing={billing}
          towingRecordId={towingRecord.id}
          onConfirmBilling={() => {
            historyPush(`/towing-record/${towingRecord.id}`)
            setSuccessNotification(
              formatMessage(messages.billingConfirmationTitle),
              formatMessage(messages.billingConfirmationMessage)
            )
          }}
          close={() => {
            historyPush(`/towing-record/${towingRecord.id}`)
            setSuccessNotification(
              formatMessage({
                id: 'towingRecord.edit.success.notification.title',
                defaultMessage: 'Changes saved',
              }),
              formatMessage({
                id: 'towingRecord.edit.success.notification.message',
                defaultMessage: 'Changes to towing record has been saved',
              })
            )
          }}
          continueEditing={() => {
            setShowConfirmationPage(false)
            historyPush(`/towing-record/${towingRecord.id}/edit`)
          }}
        />
      ) : (
        <Form>
          <SimpleRow
            margin="0.5rem 1rem 0.5rem 0.5rem"
            mobileMargin="0.5rem 0.5rem 0.5rem 0"
            justify="space-between"
          >
            <IconColumn
              direction="back"
              label="Takaisin"
              onClick={() => historyPush(`/towing-record/${towingRecord.id}`)}
            />
            <HeadingColumn>
              <Text fontWeight="700" size="big">{`Kuormakirja: ${towingRecord.recordNumber}`}</Text>
            </HeadingColumn>
          </SimpleRow>
          <TowingRecordFormFields
            formatMessage={formatMessage}
            setOrderer={setOrderer}
            hasFormChanged={hasFormChanged}
            translatedServiceTypeOptions={translatedServiceTypeOptions}
            setShowCustomerEditForm={setShowCustomerEditForm}
            initialTowingRecordValues={initialValues}
            scrollTo={scrollTo}
            disableOperator={disableOperator}
            isServiceOrderDraft={serviceOrderDraft}
            vehicleInfoCheck={towingRecord.vehicleInfoCheck}
            setOrdererFromTrafi={setOrdererFromTrafi}
            refetch={refetch}
          />
          <FormPadder>
            <ButtonRow justify="space-between">
              <ButtonColumn>
                <Button category="save" size="l" label="Tallenna" onClick={save} />
              </ButtonColumn>
              {towingRecord.type === TowingRecordType.default && canConvertToSos && !serviceOrderDraft && (
                <ButtonColumn>
                  <Button
                    category="save"
                    size="l"
                    label="Muunna SOS työksi"
                    onClick={() => setShowConvertToSosModal(true)}
                  />
                </ButtonColumn>
              )}
            </ButtonRow>
          </FormPadder>
          <ScrollToError />
        </Form>
      )}
    </Page>
  )
}

function getNewBillingData(response?: EditTowingRecordResponse): Billing | null {
  if (!response) {
    return null
  }
  if (response.editTowingRecord.__typename === 'EditTowingRecordWithBillingUpdateSuccess') {
    const billing = response.editTowingRecord.addBilling
    if (billing.__typename === 'AddBillingSuccess') {
      return billing.billing
    }
    return null
  }
  if (response.editTowingRecord.__typename === 'EditTowingRecordSuccess') {
    if (response.editTowingRecord.towingRecord.billing) {
      return response.editTowingRecord.towingRecord.billing[0]
    }
    return null
  }
  setErrorNotifications({ data: response.editTowingRecord })
  return null
}

const messages = defineMessages({
  towingRecordSuccessTitle: {
    id: 'towingRecord.edit.success.notification.title',
    defaultMessage: 'Changes saved',
  },
  towingRecordSuccessMessage: {
    id: 'towingRecord.edit.success.notification.message',
    defaultMessage: 'Changes to towing record has been saved',
  },
  addBillingErrorTitle: {
    id: 'towingRecord.add_billing.error.notification.title',
    defaultMessage: 'Unnable to save billing info',
  },
  addBillingErrorMessage: {
    id: 'towingRecord.add_billing.error.notification.message',
    defaultMessage: 'There was an error when updating billing information',
  },
  billingConfirmationTitle: {
    id: 'towingRecord.billing.confirm.notification.title',
    defaultMessage: 'Billing confirmed',
  },
  billingConfirmationMessage: {
    id: 'towingRecord.billing.confirm.notification.message',
    defaultMessage: 'Towing record saved and billing confirmed',
  },
  paymentLinkSentTitle: {
    id: 'towingRecord.billing.paymentLink.sent.title',
    defaultMessage: 'Maksulinkki lähetetty',
  },
  paymentLinkSentMessage: {
    id: 'towingRecord.billing.paymentLink.sent.message',
    defaultMessage: 'Maksulinkki on lähetetty asiakkaalle.',
  },
  toSosRecordSuccessTitle: {
    id: 'towingRecord.convert.success.notification.title',
    defaultMessage: 'Changes saved',
  },
  toSosRecordSuccessMessage: {
    id: 'towingRecord.convert.success.notification.message',
    defaultMessage: 'Changes to towing record has been saved',
  },
})

export const EditTowingRecordPage = EditTowingRecordPageIntl

export const IconColumn = styled(Navigate as any)`
  display: flex;
  // width: calc(50% - 0.5rem);
  justify-content: start;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    // width: 50%;
  }
`

export const HeadingColumn = styled.div`
  display: flex;
  // width: calc(50% - 1rem);
  justify-content: flex-end;
  // margin-right: 1rem;
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    // margin-right: 0.5rem;
    // width: calc(50% - 0.5rem);
  }
`
